import React, { useEffect, useRef, useState } from 'react';

import {
  LTButton,
  LTEmails,
  LTModal,
  LTSelect,
  LTTextarea,
} from '@lambdatestincprivate/lt-components';
import { useDispatch, useSelector } from 'react-redux';

import { useQueryParams } from '../../hooks/useQueryParams';
import { copyTextToClipboard } from '../../utils/dom';
import { EventsMap } from '../../constants/events';
import { sendAmplitudeEvents } from '../../utils/utilityFunctions';
import { linkExpiryOption } from './constant';

import { shareTest } from '../../redux/thunk/shareTest';

import Error from '../../assets/error';
import Check from '../../assets/primer-check';

import styles from './index.module.css';

const ShareModal = ({ btnRef, onModalClose, userInfo = {}, testDetails }) => {
  const [isLinkGenerationInProgress, setIsLinkGenerationInProgress] = useState(
    false
  );
  const [emails, setEmails] = useState([]);
  const [notes, setNotes] = useState('');
  const [expiresIn, setExpiresIn] = useState(3);
  const [emailFieldError, setEmailFieldError] = useState('');
  const [copyBtnText, setCopyBtnText] = useState('Copy Link');
  const [showCopyBtnIcon, setShowCopyBtnIcon] = useState(false);
  const shareData = useSelector((state) => state.shareTest?.data);
  const { shareId, shareIdUrl } = shareData || {};
  const prevShareIdRef = useRef(shareId);

  const searchParams = useQueryParams();

  const dispatch = useDispatch();

  const selectedTabSubType = searchParams.get('selectedTabSubType');
  const selectedTab = searchParams.get('selectedTab');
  const organization = userInfo?.data?.organization || {};
  const planAttributes = organization?.plan_attributes || {};
  const isAccessibilityAllowedOnTest =
    testDetails?.feature_flag?.accessibilityAllowed;
  const isAccessibilityPlanSet =
    isAccessibilityAllowedOnTest ||
    (planAttributes['AUTOMATION_ACCESSIBILITY_ENABLED'] &&
      planAttributes['AUTOMATION_ACCESSIBILITY_ENABLED'] === 1);

  useEffect(() => {
    if (prevShareIdRef.current !== shareId && shareIdUrl) {
      copyTextToClipboard(shareIdUrl);
      setIsLinkGenerationInProgress(false);
      setCopyBtnText('Link Copied');
      setShowCopyBtnIcon(true);
      setTimeout(() => {
        setCopyBtnText('Copy Link');
        setShowCopyBtnIcon(false);
      }, 1000);

      let el = document.getElementsByClassName('copyShareableLinkBtn')[0];
      if (el) {
        el.focus();
      }
    }
    prevShareIdRef.current = shareId;
  }, [shareId, shareIdUrl]);

  const handleNotesInput = (notes) => {
    setNotes(notes);
  };

  const handleExpiresInInput = (selectedOptions) => {
    setExpiresIn(selectedOptions?.[0]?.value);
  };

  const handleShareTest = (shareTestdata, isLinkCopied) => {
    const testId = testDetails?.id;

    let shareData = { ...shareTestdata };
    shareData.entityType = 'Automation Test';
    shareData.entityIds = [testId];
    shareData.showAccessibility = isAccessibilityPlanSet;
    dispatch(shareTest(shareData))
      .then(() => {
        if (!isLinkCopied) {
          onModalClose();
        }
      })
      .catch(() => {
        if (!isLinkCopied) {
          onModalClose();
        }
      });
  };

  const handleFormSubmit = () => {
    if (emails.length === 0) {
      setEmailFieldError('error');
      return;
    }
    let shareTestdata = {
      expiresAt: parseInt(expiresIn),
      emailIds: emails,
      addNote: notes,
    };
    if (selectedTabSubType) {
      shareTestdata.selectedTabSubType = selectedTabSubType;
    }
    if (selectedTab) {
      shareTestdata.selectedTab = selectedTab;
    }

    if (isAccessibilityPlanSet) {
      shareTestdata.showAccessibility = isAccessibilityPlanSet;
    }
    setEmails([]);
    setExpiresIn(3);
    setNotes('');
    setEmailFieldError('');

    handleShareTest(shareTestdata);

    // handled special case here as the focus should go on on action button
    // only once the form has no errors
    if (btnRef?.current) {
      btnRef.current.focus();
    }
  };

  const generateShareableLink = () => {
    setIsLinkGenerationInProgress(true);
    sendAmplitudeEvents(
      window.location.pathname === '/test'
        ? EventsMap.TEST_DETAILS_SHARE_GENERATE_LINK
        : EventsMap.BUILD_VIEW_SHARE_GENERATE_LINK
    );
    let shareTestdata = {
      expiresAt: parseInt(expiresIn),
    };
    if (selectedTabSubType) {
      shareTestdata.selectedTabSubType = selectedTabSubType;
    }
    if (selectedTab) {
      shareTestdata.selectedTab = selectedTab;
    }
    const organization = userInfo?.data?.organization || {};
    const planAttributes = organization?.plan_attributes || {};
    const isAccessibilityAllowedOnTest =
      testDetails?.feature_flag?.accessibilityAllowed;
    const isAccessibilityPlanSet =
      isAccessibilityAllowedOnTest ||
      (planAttributes['AUTOMATION_ACCESSIBILITY_ENABLED'] &&
        planAttributes['AUTOMATION_ACCESSIBILITY_ENABLED'] === 1);
    if (isAccessibilityPlanSet) {
      shareTestdata.showAccessibility = isAccessibilityPlanSet;
    }

    handleShareTest(shareTestdata, true);
  };

  const renderModalFooter = () => {
    return (
      <LTModal.ModalFooter
        footerElements={[
          {
            className: 'copyShareableLinkBtn',
            dataTestId: 'share-modal-copy-link-btn',
            id: 'copyShareButton',
            leadingIcon: showCopyBtnIcon ? (
              <span className={styles.checkIcon}>
                <Check />
              </span>
            ) : (
              undefined
            ),
            loaderPosition: LTButton.LoadingPosition.LEADING,
            loading: isLinkGenerationInProgress,
            onClick: () => {
              generateShareableLink();
            },
            text: copyBtnText,
            type: 'button',
            variant: 'default',
          },
          {
            dataTestId: 'share-modal-invite-btn',
            id: 'inviteButton',
            onClick: () => {
              handleFormSubmit();
            },
            text: 'Invite',
            type: 'button',
            variant: 'primary',
          },
        ]}
      />
    );
  };

  return (
    <LTModal
      id="shareModal"
      data-testid="share-modal"
      returnFocusRef={btnRef}
      onClose={onModalClose}
      title="Share"
      renderFooter={renderModalFooter()}
      noPortal={true}
    >
      <div aria-live="assertive" className="visually-hidden" role="status">
        {copyBtnText === 'Link Copied' ? 'Link Copied Successfully' : ''}
      </div>
      <div>
        {emailFieldError && (
          <div className={styles.emailError} role="alert" aria-live="assertive">
            <Error />
            Email ID not added
          </div>
        )}
      </div>
      <div className={`${styles.shareBuildFormField}`}>
        <div>
          <div className={styles.sectionHeading}>Expiry Duration</div>
          <div id="expirySelect">
            <LTSelect
              data-testid="share-modal-expiry-select"
              onChange={handleExpiresInInput}
              options={linkExpiryOption}
            />
          </div>
        </div>
        <div>
          <label
            className={`${styles.sectionHeading}`}
            for="lt-components-email-inputs-token"
          >
            Email ID(s)
            <span aria-hidden={true}>*</span>
          </label>
          <div>
            <LTEmails
              ariaRequired={true}
              autoCompleteType="off"
              data-testid="share-modal-email-input"
              hasError={emailFieldError}
              id="share-email"
              onEmailAdd={(emails) => {
                setEmails(emails);
                setEmailFieldError(false);
              }}
            />
          </div>
        </div>
        <div>
          <label className={styles.sectionHeading} for="share-message">
            Message
          </label>
          <div className="textAreaWrapper">
            <LTTextarea
              autoCompleteType="off"
              data-testid="share-modal-message-textarea"
              extraStyle={{
                boxSizing: 'border-box',
                width: '100%',
              }}
              id="share-message"
              onChange={handleNotesInput}
              placeholder="Add Message (Optional)"
            />
          </div>
        </div>
      </div>
    </LTModal>
  );
};

export default ShareModal;
